/**
 * DecisionFlow — RoleLibraryPopup
 * ui/src/components/admin/RoleLibraryPopup.jsx
 * S615
 *
 * Know More popup for platform role archetypes.
 * Four sections: What This Role Does / How They Sell / What Good Looks Like / When To Use This
 * CTA: "Add to My Roles →" (disabled if is_adopted)
 *
 * Spec authority: crl_spec_v1_0.md §9
 * Props:
 *   role       — ref_role_library row (full)
 *   motions    — tenant selling_motion[] for chip rendering
 *   isAdopted  — bool
 *   onAdopt    — () => void
 *   onClose    — () => void
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded Tailwind. Zero dark: variants.
 * No <form> tags.
 */

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X, ArrowRight, CheckCircle, Zap } from "lucide-react";

function MotionChip({ label }) {
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium border border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))]">
      {label}
    </span>
  );
}

function Section({ number, title, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[rgb(var(--color-primary))]/10 text-[10px] font-bold text-[rgb(var(--color-primary))]">
          {number}
        </span>
        <h4 className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">{title}</h4>
      </div>
      <div className="text-tok-sm text-[rgb(var(--color-text))] leading-relaxed pl-7">
        {children}
      </div>
    </div>
  );
}

export default function RoleLibraryPopup({ role, motions, isAdopted, onAdopt, onClose }) {
  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Resolve primary motion name from motions array
  const primaryMotion = motions.find(m => m.motion_code === role.primary_motion_code);
  // industry_tags arrives as JSON string from DB jsonb — parse defensively
  let industryTags = [];
  if (Array.isArray(role.industry_tags)) {
    industryTags = role.industry_tags;
  } else if (typeof role.industry_tags === "string") {
    try { industryTags = JSON.parse(role.industry_tags); } catch { industryTags = []; }
  }

  return createPortal(
    /* Backdrop */
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Panel — 560px, scrollable body, fixed footer */}
      <div className="relative flex flex-col w-[560px] max-h-[85vh] rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between border-b border-[rgb(var(--color-border))] px-6 py-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))] truncate">{role.display_name}</h3>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
              {role.archetype} archetype
              {industryTags.length ? ` · ${industryTags.join(" / ")}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-4 shrink-0 rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Meta chips */}
        <div className="flex flex-wrap items-center gap-2 border-b border-[rgb(var(--color-border))] px-6 py-3">
          {primaryMotion && <MotionChip label={primaryMotion.motion_name} />}
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">·</span>
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {role.quota_bearing ? "✓ Quota Bearing" : "Non-Quota"}
          </span>
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">·</span>
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{role.direct_indirect}</span>
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">·</span>
          <span className="inline-flex items-center gap-1 text-tok-xs text-[rgb(var(--color-primary))]">
            <Zap className="h-3 w-3" /> data-fit Curated · {role.platform_version ?? "v1.0"}
          </span>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          <Section number="1" title="What This Role Does">
            {role.mandate}
          </Section>

          <Section number="2" title="How They Sell">
            {role.behavior_description}
          </Section>

          <Section number="3" title="What Good Looks Like">
            {role.expected_results}
          </Section>

          <Section number="4" title="When To Use This">
            {role.applicability}
          </Section>

          {role.not_recommended_for && (
            <div className="rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3">
              <p className="text-tok-xs font-semibold text-[rgb(var(--color-warning))] mb-1">Not recommended for</p>
              <p className="text-tok-xs text-[rgb(var(--color-text))] leading-relaxed">{role.not_recommended_for}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-[rgb(var(--color-border))] px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
          >
            Cancel
          </button>
          {isAdopted ? (
            <span className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] cursor-not-allowed">
              <CheckCircle className="h-3.5 w-3.5 text-[rgb(var(--color-success))]" /> Already Added
            </span>
          ) : (
            <button
              type="button"
              onClick={onAdopt}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-medium text-white hover:opacity-90 transition-opacity"
            >
              Add to My Roles <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
